import { Options as HighchartsOptions } from 'highcharts';

import { CHART_HEIGHT } from '../../config';

export const PLOT_BAND_COLOR = '#ffe8e8';
export const WARNING_BACKGROUND_COLOR = '#e02020';

const TOOLTIP_DISTANCE_FROM_POINT = 20;
const TOOLTIP_Y_POSITION_THRESHOLD = 7;

export const BENCHMARK_SLUG = '__benchmark';

export const StackType = {
  REQUIRED: 'required',
  STAFFED: 'staffed',
} as const;

export const staticChartOptions: HighchartsOptions = {
  boost: {
    enabled: true,
    useGPUTranslations: true,
  },
  credits: {
    enabled: false,
  },
  chart: {
    height: CHART_HEIGHT,
    spacingBottom: 7,
    spacingLeft: 0,
    spacingRight: 0,
    style: {
      fontFamily: 'Roboto, sans-serif',
    },
    type: 'column',
  },
  legend: {
    enabled: false,
  },
  plotOptions: {
    column: {
      borderWidth: 0,
      groupPadding: 0.07,
      pointPadding: 0.05,
      pointWidth: 12.5,
      stacking: 'normal',
    },
    series: {
      /**
       * Use Infinity to enable chart to use animation when series of multiple queues are visible
       * see https://api.highcharts.com/highcharts/plotOptions.series.animationLimit
       */
      animationLimit: Number.POSITIVE_INFINITY,
    },
  },
  series: [
    {
      type: 'column',
      states: {
        hover: {
          brightness: 0,
        },
      },
    },
  ],
  title: {
    text: '',
  },
  tooltip: {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    outside: true,
    positioner(labelWidth, labelHeight, point) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const { chart } = this; // `this` is of type `TooltipOptions`
      const { left, top } = chart.pointer.chartPosition;
      const chartBottomPosition = top + chart.chartHeight - TOOLTIP_Y_POSITION_THRESHOLD;
      let x = point.plotX + left + TOOLTIP_DISTANCE_FROM_POINT;
      let y = point.plotY + labelHeight + TOOLTIP_DISTANCE_FROM_POINT;

      if (window.innerWidth <= x + labelWidth + 25) {
        x = point.plotX + left - labelWidth - TOOLTIP_DISTANCE_FROM_POINT;
      }

      const minY = top - TOOLTIP_DISTANCE_FROM_POINT;

      if (y + labelHeight >= chartBottomPosition) {
        y = Math.max(chartBottomPosition - labelHeight, minY);
      } else if (y < minY) {
        y = minY;
      }

      return {
        x,
        y,
      };
    },
    shadow: false,
    shared: true,
    useHTML: true,
  },
  xAxis: {
    currentDateIndicator: false,
    grid: {
      enabled: false,
    },
    labels: {
      enabled: false,
    },
    maxPadding: 0,
    minPadding: 0,
    tickLength: 0,
    title: {
      text: '',
    },
    type: 'datetime',
  },
  yAxis: {
    labels: {
      enabled: false,
    },
    grid: {
      enabled: false,
    },
    showEmpty: false,
    tickAmount: 6,
    title: { text: '' },
  },
};

export const staticSLAChartOptions: HighchartsOptions = {
  ...staticChartOptions,
  chart: {
    height: CHART_HEIGHT + 3, // to prevent overflow of crosshair
    spacingBottom: 10, // 7 + 3 - to match designs used for scheduling chart + handle overflow of crosshair
    spacingLeft: 0,
    spacingRight: 0,
    spacingTop: 0,
    style: {
      fontFamily: 'Roboto, sans-serif',
    },
    type: 'column',
  },
  plotOptions: {
    line: {
      marker: {
        enabled: false,
        lineWidth: 1,
        radius: 4,
        symbol: 'circle',
      },
    },
    series: {
      animation: {
        duration: 300,
      },
      /**
       * Use Infinity to enable chart to use animation when series of multiple queues are visible
       * see https://api.highcharts.com/highcharts/plotOptions.series.animationLimit
       */
      animationLimit: Number.POSITIVE_INFINITY,
      stickyTracking: false,
    },
  },
  series: [
    {
      type: 'line',
      step: 'left',
      states: {
        hover: {
          brightness: 0,
          enabled: true,
        },
      },
    },
  ],
  tooltip: {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    distance: 0,
    outside: true,
    positioner(labelWidth, labelHeight, point) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const { chart } = this; // `this` is of type `TooltipOptions`
      const { left, top } = chart.pointer.chartPosition;
      const chartBottomPosition = top + chart.chartHeight - TOOLTIP_Y_POSITION_THRESHOLD;
      let x = point.plotX + left + TOOLTIP_DISTANCE_FROM_POINT;
      let y = point.plotY + labelHeight + TOOLTIP_DISTANCE_FROM_POINT;

      if (window.innerWidth <= x + labelWidth + 25) {
        x = point.plotX + left - labelWidth - TOOLTIP_DISTANCE_FROM_POINT;
      }

      const minY = top - TOOLTIP_DISTANCE_FROM_POINT;

      if (y + labelHeight >= chartBottomPosition) {
        y = Math.max(chartBottomPosition - labelHeight, minY);
      } else if (y < minY) {
        y = minY;
      }

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore reset chart position to fix not moving marker for hovered point when scrolling on x-axis
      this.chart.pointer.chartPosition = null;

      return {
        x,
        y,
      };
    },
    snap: 5,
    shadow: false,
    shape: 'square',
    shared: true,
    useHTML: true,
  },
  yAxis: {
    ...staticChartOptions.yAxis,
    endOnTick: false,
  },
};
